<script setup lang="ts">
import {ref, inject, computed, watch, provide} from 'vue';
import FileTree from './components/FileTree.vue';
import FilePreview from './components/FilePreview.vue';
import ToolBar from './components/ToolBar.vue';
import DiffListPanel from './components/diff-list/DiffListPanel.vue';
import WarningMessages from '../components/WarningMessages.vue';
import {SvgIcon} from '../svg.ts';
import {createInspectorDiffTreeState} from '../components/inspector_diff_tree.ts';
import type {Store, RawWarning} from './store.ts';
import {useDiffConfigBanner} from './components/use-diff-config-banner.ts';
import {useSidebarResize} from './components/use-sidebar-resize.ts';

const store = inject<Store>('store');
const sidebarVisible = ref(true);
const searchQuery = ref('');
const inspectorDiffTreeState = createInspectorDiffTreeState();
provide('inspectorDiffTreeState', inspectorDiffTreeState);
const {sidebarWidth, handleStartResize} = useSidebarResize('left');
const {hasCustomDiffConfig, resetDiffConfig, previewResetKey} = useDiffConfigBanner(store);

// Template ref
const warnings = ref(null);

const toolbarWarnings = ref([]);
const previewWarnings = ref([]);

const allWarnings = computed(() => {
  return (store.warnings || []).concat(toolbarWarnings.value.concat(previewWarnings.value));
});

watch(allWarnings, () => {
  warnings.value.replaceWarnings(allWarnings.value);
});

function handleToolbarWarnings(warnings: RawWarning[]) {
  toolbarWarnings.value = warnings;
}

function handlePreviewWarnings(warnings: RawWarning[]) {
  previewWarnings.value = warnings;
}
</script>

<template>
  <WarningMessages
    ref="warnings"
    :repo-link="store.repoLink"
    :branch-name="store.refLinkSnippet"
  />
  <div
    v-if="hasCustomDiffConfig"
    class="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2.5 tw:border-b tw:text-sm"
    style="background-color: var(--color-secondary-alpha-40); border-color: var(--color-secondary);"
  >
    <SvgIcon
      name="octicon-filter"
      :size="14"
    />
    <span>Custom diff settings applied</span>
    <button
      class="tw:text-primary tw:underline tw:cursor-pointer tw:bg-transparent tw:border-0"
      @click="resetDiffConfig"
    >
      Reset
    </button>
  </div>
  <ToolBar
    v-model:sidebar-visible="sidebarVisible"
    v-model:search-query="searchQuery"
    :sidebar-width="sidebarWidth"
    @warnings="handleToolbarWarnings"
  />
  <div class="tw:flex tw:flex-row tw:w-full">
    <!-- Project Side Bar -->
    <FileTree
      v-if="sidebarVisible && store.fileTree !== undefined"
      v-model:search-query="searchQuery"
      :tree="store.fileTree.items"
      :sidebar-width="sidebarWidth"
      :handle-start-resize="handleStartResize"
    />

    <!-- Main Content -->
    <div class="tw:w-full tw:m-1">
      <!--
        Note: key here ensures we fully unmount the component and remount
        another, we need to do this because we manually modify the DOM and
        VueJS doesn't know how to handle that.
      -->
      <FilePreview
        :key="`${store.activePath.value}-${previewResetKey}`"
        v-model:preview="store.activePreview.value"
        @warnings="handlePreviewWarnings"
      />
    </div>

    <!-- Diff List Sidebar -->
    <DiffListPanel />
  </div>
</template>
